import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Lightbox from 'react-image-lightbox';

interface Props {
    imagesUrls: string[];
    justOnceImage?: boolean;
}

const ShowNoteImages = ({ imagesUrls, justOnceImage = false }: Props) => {
    const [showImageDialogState, setShowImageDialogState] = useState<boolean>(false);
    const [imageIndex, setImageIndex] = useState<number>(0);

    const firstTwoImages = useMemo(() => imagesUrls.slice(0, 2), [imagesUrls]);
    const firstThreeImages = useMemo(() => imagesUrls.slice(0, 3), [imagesUrls]);

    const hasMoreThanThreeImages = useMemo(() => imagesUrls.length > 3, [imagesUrls]);

    const handleShowImageDialogOpen = (index: number) => {
        setShowImageDialogState(true);
        setImageIndex(index);
    };

    const handleShowImageDialogClose = () => {
        setShowImageDialogState(false);
        setImageIndex(0);
    };

    const handleNextImageClick = () => {
        setImageIndex((previousIndex) => (previousIndex + 1) % imagesUrls.length);
    };

    const handlePrevImageClick = () => {
        setImageIndex((previousIndex) => (previousIndex + imagesUrls.length - 1) % imagesUrls.length);
    };

    return (
        <Box
            sx={{
                display: 'grid',
                gap: 1,
                gridTemplateColumns: justOnceImage ? 'auto' : `repeat(3, 1fr)`,
                px: 1,
            }}
        >
            {(hasMoreThanThreeImages ? firstTwoImages : firstThreeImages).map((imageUrl, index) => (
                <Box
                    key={imageUrl}
                    onClick={() => handleShowImageDialogOpen(index)}
                    sx={{
                        display: 'grid',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        borderRadius: 1,
                        ':hover': {
                            cursor: 'pointer',
                        },
                    }}
                >
                    <Box
                        component='img'
                        src={imageUrl}
                        key={`${imageUrl}${index}`}
                        sx={{
                            width: 'auto',
                            maxWidth: '100%',
                            height: 'auto',
                            maxHeight: 150,
                            borderRadius: 1,
                        }}
                    />
                </Box>
            ))}

            {hasMoreThanThreeImages && (
                <Box
                    sx={{
                        alignSelf: 'center',
                        justifySelf: 'center',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 30,
                            opacity: '54%',
                            color: 'black',
                        }}
                    >
                        +{imagesUrls.length - 2}
                    </Typography>
                </Box>
            )}

            {showImageDialogState && (
                <Lightbox
                    mainSrc={imagesUrls[imageIndex]}
                    nextSrc={imagesUrls[(imageIndex + 1) % imagesUrls.length]}
                    prevSrc={imagesUrls[(imageIndex + imagesUrls.length - 1) % imagesUrls.length]}
                    onCloseRequest={handleShowImageDialogClose}
                    onMovePrevRequest={handlePrevImageClick}
                    onMoveNextRequest={handleNextImageClick}
                />
            )}
        </Box>
    );
};

export default ShowNoteImages;
